import { Box, CircularProgress, Container, Grid, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import CustomerDetails from 'src/components/form/CustomerDetails';
import OrderForm from 'src/components/form/OrderForm';
import HorizontalStepper from 'src/components/stepper/HorizontalStepper';
import truck from '../assets/truck.png';
import { GET_ORDER_BY_ID } from 'src/apiService/apiDeclaration';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { USER_ROLES } from 'src/config/enum';
import React from 'react';

const OrderDetails = () => {
  const location = useLocation();
  const data = location.state || {};
  const [state, setState] = useState();
  const [customerInfo, setCustomerInfo] = useState();
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);

  // Memoize the order ID to prevent unnecessary recalculations
  const orderId = useMemo(() => {
    return data.orderId ? data.orderId : data?._id;
  }, [data.orderId, data?._id]);

  const getOrder = useCallback(async (id) => {
    setLoading(true);
    try {
      let resp = await GET_ORDER_BY_ID(id);
      setState(resp.data);
      setCustomerInfo(resp.data.customerId);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Memoize onUpdateOrder to prevent OrderForm re-renders
  const onUpdateOrder = useCallback(() => {
    getOrder(orderId);
  }, [getOrder, orderId]);

  useEffect(() => {
    getOrder(orderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
      <Container
        sx={{ pt: 3 }}
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box>{children}</Box>}
      </Container>
    );
  }

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const [value, setValue] = useState(0);

  const handleChange = useCallback((event, newValue) => {
    setValue(newValue);
  }, []);

  // Memoize the render content to prevent unnecessary re-renders
  const renderContent = useMemo(() => {
    if (loading && !state?._id) {
      return (
        <Box sx={{ display: 'flex', height: '50vh', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      );
    } else if (!loading && !state?._id) {
      return (
        <Box
          sx={{
            display: 'flex',
            height: '50vh',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 200 }}>
            {/* Something Went Wrong*/}
          </Typography>
        </Box>
      );
    } else {
      return <OrderForm state={state} setState={setState} customerInfo={customerInfo} onUpdateOrder={onUpdateOrder} />;
    }
  }, [loading, state, customerInfo, onUpdateOrder]);

  // Memoize the header content
  const headerContent = useMemo(() => {
    if (!state?.status) return null;

    return (
      <>
        <Typography variant={'h3'} sx={{ color: 'white', px: 4, py: 1, fontWeight: 600 }}>
          {t(`taglines.${state?.status[state.status.length - 1].status}`)}
        </Typography>
        <Grid container xs={12} md={10}>
          <HorizontalStepper status={state?.status} isSubOrder={false} />
        </Grid>
      </>
    );
  }, [state?.status]);

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          pb: { md: 0, xs: 2 },
          width: '100%',
          minHeight: '12rem',
          borderRadius: '12px',
          bgcolor: '#88C070',
        }}
      >
        <Box sx={{ width: { md: '80%', xs: '100%' }, display: 'flex', flexDirection: 'column' }}>{headerContent}</Box>
        <Grid container md={3} sx={{ display: { md: 'flex', xs: 'none' } }}>
          <img
            src={truck}
            style={{ width: '100%', color: 'white', height: '100%', objectFit: 'contain', objectPosition: 'center' }}
            alt="truck"
          />
        </Grid>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
          <Tab label={t('customerPage.orderDetails')} {...a11yProps(0)} />
          {user.role !== USER_ROLES.OPERATOR && <Tab label={t('orderDetailsPage.customerDetails')} {...a11yProps(1)} />}
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        {renderContent}
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <CustomerDetails data={customerInfo} />
      </CustomTabPanel>
    </Container>
  );
};
// Memoize the component to prevent unnecessary re-renders
export default React.memo(OrderDetails);
